import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import InscriptionsList from './components/InscriptionsList';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_KEY = process.env.REACT_APP_API_KEY || process.env.REACT_APP_API_SECRET || 'aji-tanjeh-secret-2024';
const ENABLE_API_AUTH = process.env.REACT_APP_ENABLE_API_AUTH === 'true';

// Helper function pour les requêtes API avec headers
const apiFetch = async (url, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  // Ajouter le header d'authentification seulement si activé
  if (ENABLE_API_AUTH) {
    headers['x-api-key'] = API_KEY;
  }

  const response = await fetch(url, {
    ...options,
    headers
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erreur réseau' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

// Composant de connexion
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Vérification locale (les credentials sont stockés dans les variables d'environnement)
    const validUsername = process.env.REACT_APP_ADMIN_USERNAME || 'admin';
    const validPassword = process.env.REACT_APP_ADMIN_PASSWORD || 'ajitanjeh2024';

    if (username === validUsername && password === validPassword) {
      // Stocker la session
      sessionStorage.setItem('isAuthenticated', 'true');
      sessionStorage.setItem('authTime', Date.now().toString());
      onLogin();
    } else {
      setError('اسم المستخدم أو كلمة المرور غير صحيحة');
    }
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>🎓 أجي تنجح</h1>
          <p>لوحة تحكم الإدارة</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>اسم المستخدم</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>كلمة المرور</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
            {loading ? '⏳ جاري التحقق...' : '🔐 تسجيل الدخول'}
          </button>
        </form>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [inscriptions, setInscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState({});
  const [qrCodes, setQrCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [filters, setFilters] = useState({ statut: '', niveau: '', typeInscription: '', search: '' });
  const [searchInput, setSearchInput] = useState(''); // Pour le debounce
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [processingIds, setProcessingIds] = useState(new Set()); // IDs en cours de traitement
  const [generatingPDF, setGeneratingPDF] = useState(false); // État pour la génération de PDF

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const authStatus = sessionStorage.getItem('isAuthenticated');
    const authTime = sessionStorage.getItem('authTime');
    
    // Session expire après 24 heures
    const SESSION_DURATION = 24 * 60 * 60 * 1000;
    
    if (authStatus === 'true' && authTime) {
      const elapsed = Date.now() - parseInt(authTime);
      if (elapsed < SESSION_DURATION) {
        setIsAuthenticated(true);
      } else {
        // Session expirée
        sessionStorage.removeItem('isAuthenticated');
        sessionStorage.removeItem('authTime');
      }
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('authTime');
    setIsAuthenticated(false);
  };

  // Charger les données
  const fetchData = useCallback(async () => {
    try {
      // Inscriptions
      let url = `${API_URL}/inscriptions`;
      const params = new URLSearchParams();
      if (filters.statut) params.append('statut', filters.statut);
      if (filters.niveau) params.append('niveau', filters.niveau);
      if (filters.typeInscription) params.append('typeInscription', filters.typeInscription);
      if (filters.search) params.append('search', filters.search);
      if (params.toString()) url += `?${params.toString()}`;

      const [inscData, statsData, statusData] = await Promise.all([
        apiFetch(url),
        apiFetch(`${API_URL}/inscriptions/stats`),
        apiFetch(`${API_URL}/inscriptions/whatsapp/status`)
      ]);

      if (inscData.success) setInscriptions(inscData.data);
      if (statsData.success) setStats(statsData.data);
      if (statusData.success) setWhatsappStatus(statusData.data);

    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Charger les QR codes
  const fetchQRCodes = async () => {
    try {
      const data = await apiFetch(`${API_URL}/inscriptions/whatsapp/qrcodes`);
      if (data.success) setQrCodes(data.data);
    } catch (error) {
      console.error('Erreur chargement QR codes:', error);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Rafraîchir toutes les 10s
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (showQR) {
      fetchQRCodes();
      const interval = setInterval(fetchQRCodes, 5000); // Rafraîchir QR toutes les 5s
      return () => clearInterval(interval);
    }
  }, [showQR]);

  // Générer les PDFs groupés par agence
  const generatePaymentReceiptsPDF = async (agency = null) => {
    setGeneratingPDF(true);
    try {
      const url = agency 
        ? `${API_URL}/inscriptions/payment-receipts/pdf?agency=${agency}`
        : `${API_URL}/inscriptions/payment-receipts/pdf`;
      
      const data = await apiFetch(url, { method: 'GET' });
      
      if (data.success && data.data.pdfs) {
        // Télécharger chaque PDF généré (route publique, pas besoin d'authentification)
        for (const [agencyName, pdfInfo] of Object.entries(data.data.pdfs)) {
          const downloadUrl = `${API_URL}/inscriptions/payment-receipts/pdf/${agencyName}/${pdfInfo.filename}`;
          
          // Télécharger directement avec un lien (route publique)
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = pdfInfo.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Petit délai entre les téléchargements
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        alert(`✅ تم إنشاء ${Object.keys(data.data.pdfs).length} ملف PDF بنجاح`);
      } else {
        alert('❌ خطأ: ' + (data.error || 'لا توجد وصلات للأداء متاحة'));
      }
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      alert('❌ حدث خطأ أثناء إنشاء ملفات PDF: ' + error.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
    }, 500); // Attendre 500ms après que l'utilisateur arrête de taper

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Valider une inscription
  const handleValidate = async (id, filiere) => {
    // Empêcher les clics multiples
    if (processingIds.has(id)) {
      console.log('Validation déjà en cours pour:', id);
      return;
    }

    console.log('handleValidate called:', id, filiere);
    
    // Marquer comme en cours de traitement
    setProcessingIds(prev => new Set(prev).add(id));
    
    try {
      const data = await apiFetch(`${API_URL}/inscriptions/${id}/validate`, {
        method: 'PUT',
        body: JSON.stringify({ filiere })
      });
      console.log('Validation response:', data);
      if (data.success) {
        fetchData();
        setSelectedInscription(null);
        alert('✅ تم التحقق من التسجيل وإرسال روابط المجموعات');
      } else {
        alert('❌ خطأ: ' + (data.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      console.error('Erreur validation:', error);
      alert('❌ حدث خطأ أثناء التحقق: ' + error.message);
    } finally {
      // Retirer de la liste des IDs en traitement
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  // Rejeter une inscription
  const handleReject = async (id) => {
    // Empêcher les clics multiples
    if (processingIds.has(id)) {
      console.log('Rejet déjà en cours pour:', id);
      return;
    }

    console.log('handleReject called:', id);
    if (!window.confirm('هل أنت متأكد من رفض هذا التسجيل؟')) return;
    
    // Marquer comme en cours de traitement
    setProcessingIds(prev => new Set(prev).add(id));
    
    try {
      const data = await apiFetch(`${API_URL}/inscriptions/${id}/reject`, {
        method: 'PUT'
      });
      console.log('Reject response:', data);
      if (data.success) {
        fetchData();
        setSelectedInscription(null);
        alert('تم رفض التسجيل');
      } else {
        alert('❌ خطأ: ' + (data.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      console.error('Erreur rejet:', error);
      alert('❌ حدث خطأ: ' + error.message);
    } finally {
      // Retirer de la liste des IDs en traitement
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  // Marquer que l'étudiant a rejoint les groupes
  const handleMarkJoinedGroups = async (id) => {
    if (!window.confirm('هل أنت متأكد أن الطالب قد انضم إلى المجموعات؟ سيتم حذف صورة فيسبوك بعد التأكيد.')) {
      return;
    }
    
    // Empêcher les clics multiples
    if (processingIds.has(id)) {
      console.log('Mark joined groups déjà en cours pour:', id);
      return;
    }
    
    // Marquer comme en cours de traitement
    setProcessingIds(prev => new Set(prev).add(id));
    
    try {
      const data = await apiFetch(`${API_URL}/inscriptions/${id}/mark-joined-groups`, {
        method: 'PUT'
      });
      console.log('Mark joined groups response:', data);
      if (data.success) {
        await fetchData();
        // Mettre à jour l'inscription sélectionnée si elle est ouverte
        if (selectedInscription && selectedInscription._id === id) {
          setSelectedInscription(data.data);
        }
        alert('✅ تم تأكيد انضمام الطالب إلى المجموعات وتم حذف الصور');
      } else {
        alert('❌ خطأ: ' + (data.error || 'خطأ غير معروف'));
      }
    } catch (error) {
      console.error('Erreur mark joined groups:', error);
      alert('❌ حدث خطأ أثناء التأكيد: ' + error.message);
    } finally {
      // Retirer de la liste des IDs en traitement
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  // Supprimer une inscription
  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التسجيل نهائياً؟')) return;
    
    try {
      const data = await apiFetch(`${API_URL}/inscriptions/${id}`, {
        method: 'DELETE'
      });
      if (data.success) {
        fetchData();
        setSelectedInscription(null);
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const statusLabels = {
    nouveau: 'جديد',
    info_collectees: 'تم جمع المعلومات',
    photo_recue: 'تم استلام الصورة',
    rib_envoye: 'تم إرسال RIB',
    virement_en_attente: 'في انتظار التحقق',
    valide: 'تم التأكيد ✓',
    verifie: 'تم التحقق ✓✓',
    rejete: 'مرفوض'
  };

  // Si non authentifié, afficher l'écran de connexion
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-title">
          <span className="emoji">🎓</span>
          <h1>لوحة تحكم أجي تنجح</h1>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-primary" 
            onClick={() => generatePaymentReceiptsPDF()}
            disabled={generatingPDF}
            title="إنشاء ملفات PDF لوصلات الأداء مجمعة حسب الوكالة"
          >
            {generatingPDF ? '⏳ جاري الإنشاء...' : '📄 إنشاء PDFs للوصلات'}
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowQR(!showQR)}
          >
            📱 {showQR ? 'إخفاء QR Codes' : 'عرض QR Codes'}
          </button>
          <button 
            className="btn btn-primary"
            onClick={fetchData}
          >
            🔄 تحديث
          </button>
          <button 
            className="btn btn-danger"
            onClick={handleLogout}
          >
            🚪 تسجيل الخروج
          </button>
        </div>
      </header>

      {/* QR Codes Section */}
      {showQR && (
        <div className="qr-section">
          <h2>📱 QR Codes للاتصال بـ WhatsApp</h2>
          <div className="qr-grid">
            {['3AC', '1BAC', '2BAC'].map(niveau => (
              <div key={niveau} className="qr-card">
                <h3>{niveau}</h3>
                {whatsappStatus[niveau]?.isReady ? (
                  <div className="qr-status connected">
                    <span className="qr-status-dot"></span>
                    متصل ✓
                  </div>
                ) : qrCodes[niveau] ? (
                  <>
                    <img src={qrCodes[niveau]} alt={`QR ${niveau}`} />
                    <div className="qr-status waiting">
                      <span className="qr-status-dot"></span>
                      في انتظار المسح
                    </div>
                  </>
                ) : (
                  <div className="qr-status waiting">
                    <span className="qr-status-dot"></span>
                    جاري التحميل...
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total || 0}</div>
            <div className="stat-label">إجمالي التسجيلات</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.today || 0}</div>
            <div className="stat-label">تسجيلات اليوم</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.byStatut?.virement_en_attente || 0}</div>
            <div className="stat-label">في انتظار التحقق</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.byStatut?.valide || 0}</div>
            <div className="stat-label">تم التحقق</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <div className="filter-group search-group">
          <label>🔍 البحث:</label>
          <input
            type="text"
            placeholder="ابحث بالاسم أو رقم الهاتف..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <label>الحالة:</label>
          <select 
            value={filters.statut}
            onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
          >
            <option value="">الكل</option>
            <option value="not_confirmed">لم يتم التأكيد بعد</option>
            <option value="confirmed">تم التأكيد (التحقق من التحويل)</option>
            <option value="added">تم الإضافة للمجموعات</option>
          </select>
        </div>
        <div className="filter-group">
          <label>المستوى:</label>
          <select 
            value={filters.niveau}
            onChange={(e) => setFilters({ ...filters, niveau: e.target.value })}
          >
            <option value="">الكل</option>
            <option value="3AC">3AC</option>
            <option value="1BAC">1BAC</option>
            <option value="2BAC">2BAC</option>
          </select>
        </div>
        <div className="filter-group">
          <label>نوع التسجيل:</label>
          <select 
            value={filters.typeInscription || ''}
            onChange={(e) => setFilters({ ...filters, typeInscription: e.target.value || null })}
          >
            <option value="">الكل</option>
            <option value="moaskar">🔥 معسكر</option>
            <option value="aard_assasi">📚 عرض أساسي</option>
          </select>
        </div>
      </div>

      {/* Inscriptions List */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <InscriptionsList
          inscriptions={inscriptions}
          statusLabels={statusLabels}
          onSelect={setSelectedInscription}
          onValidate={handleValidate}
          onReject={handleReject}
          onDelete={handleDelete}
          apiUrl={API_URL}
        />
      )}

      {/* Detail Modal */}
      {selectedInscription && (
        <InscriptionModal
          inscription={selectedInscription}
          statusLabels={statusLabels}
          onClose={() => setSelectedInscription(null)}
          onValidate={handleValidate}
          onReject={handleReject}
          onMarkJoinedGroups={handleMarkJoinedGroups}
          apiUrl={API_URL}
          isProcessing={processingIds.has(selectedInscription._id)}
          onRefresh={fetchData}
        />
      )}
    </div>
  );
}

// Modal Component
function InscriptionModal({ inscription: initialInscription, statusLabels, onClose, onValidate, onReject, onMarkJoinedGroups, apiUrl, isProcessing = false, onRefresh }) {
  const [inscription, setInscription] = useState(initialInscription);
  const [filiere, setFiliere] = useState(inscription.filiere || 'scientifique');
  const [sending, setSending] = useState(false);
  const showFiliereSelect = ['1BAC', '2BAC'].includes(inscription.niveauScolaire);
  
  // États pour les informations de paiement
  const [paymentType, setPaymentType] = useState(inscription.paymentInfo?.paymentType || '');
  const [agencyName, setAgencyName] = useState(inscription.paymentInfo?.agencyName || '');
  const [selectedReceiptIndex, setSelectedReceiptIndex] = useState(inscription.paymentInfo?.selectedReceiptIndex ?? null);
  
  // Vérifier si une image est en attente
  const hasPendingImageRequest = inscription.pendingImageRequest?.type && !inscription.pendingImageRequest?.received;
  const pendingImageType = inscription.pendingImageRequest?.type;
  
  // Calculer le nombre total d'images de reçu disponibles
  const receiptImages = [];
  let currentIndex = 0;
  
  // Ajouter screenshotVirement en premier s'il existe
  if (inscription.screenshotVirement?.filepath) {
    receiptImages.push({ 
      index: currentIndex++, 
      type: 'screenshotVirement', 
      label: 'وصل الأداء الرئيسي' 
    });
  }
  
  // Ajouter les images temporaires de type recu ou unknown
  inscription.imagesTemporaires?.forEach((img, idx) => {
    if (img.type === 'recu' || img.type === 'unknown') {
      receiptImages.push({ 
        index: currentIndex++, 
        type: 'temp', 
        tempIndex: idx,
        label: `صورة ${idx + 1}` 
      });
    }
  });

  // Demander renvoi d'image
  const requestImageResend = async (imageType) => {
    const reason = window.prompt(
      imageType === 'facebook' 
        ? 'سبب طلب إعادة صورة فيسبوك (اختياري):' 
        : 'سبب طلب إعادة وصل الأداء (اختياري):'
    );
    
    if (reason === null) return; // User cancelled
    
    setSending(true);
    try {
      const data = await apiFetch(`${apiUrl}/inscriptions/${inscription._id}/request-image`, {
        method: 'POST',
        body: JSON.stringify({ imageType, reason })
      });
      if (data.success) {
        alert('✅ تم إرسال الطلب للمستخدم');
        // Mettre à jour l'état local
        setInscription(prev => ({
          ...prev,
          pendingImageRequest: data.pendingImageRequest
        }));
        // Rafraîchir les données
        if (onRefresh) onRefresh();
      } else {
        alert('❌ خطأ: ' + data.error);
      }
    } catch (error) {
      alert('❌ خطأ في الاتصال');
    }
    setSending(false);
  };

  // Envoyer message personnalisé
  const sendCustomMessage = async () => {
    const message = window.prompt('أدخل الرسالة:');
    if (!message) return;
    
    setSending(true);
    try {
      const data = await apiFetch(`${apiUrl}/inscriptions/${inscription._id}/send-message`, {
        method: 'POST',
        body: JSON.stringify({ message })
      });
      if (data.success) {
        alert('✅ تم إرسال الرسالة');
      } else {
        alert('❌ خطأ: ' + data.error);
      }
    } catch (error) {
      alert('❌ خطأ في الاتصال');
    }
    setSending(false);
  };

  // Supprimer la conversation
  const deleteConversation = async () => {
    const confirm = window.confirm('هل أنت متأكد من حذف المحادثة؟ سيتم إعادة تعيين حالة التسجيل.');
    if (!confirm) return;
    
    setSending(true);
    try {
      const data = await apiFetch(`${apiUrl}/inscriptions/${inscription._id}/conversation`, {
        method: 'DELETE'
      });
      if (data.success) {
        alert('✅ تم حذف المحادثة وإعادة تعيين التسجيل');
        // Mettre à jour l'inscription locale
        setInscription(data.data);
        // Rafraîchir les données
        if (onRefresh) onRefresh();
      } else {
        alert('❌ خطأ: ' + data.error);
      }
    } catch (error) {
      alert('❌ خطأ في الاتصال');
    }
    setSending(false);
  };
  
  // Sauvegarder les informations de paiement
  const savePaymentInfo = async () => {
    if (!paymentType) {
      alert('⚠️ يرجى اختيار نوع الدفع (بنك أو وكالة)');
      return;
    }
    
    if (paymentType === 'agency' && !agencyName) {
      alert('⚠️ يرجى اختيار اسم الوكالة');
      return;
    }
    
    setSending(true);
    try {
      const data = await apiFetch(`${apiUrl}/inscriptions/${inscription._id}/payment-info`, {
        method: 'PUT',
        body: JSON.stringify({
          paymentType,
          agencyName: paymentType === 'agency' ? agencyName : null,
          selectedReceiptIndex: selectedReceiptIndex !== null ? selectedReceiptIndex : null
        })
      });
      if (data.success) {
        alert('✅ تم حفظ معلومات الدفع بنجاح');
        // Mettre à jour l'inscription locale
        setInscription(data.data);
        // Rafraîchir les données
        if (onRefresh) onRefresh();
      } else {
        alert('❌ خطأ: ' + data.error);
      }
    } catch (error) {
      alert('❌ خطأ في الاتصال');
    }
    setSending(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📋 تفاصيل التسجيل</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-label">الاسم الكامل</div>
              <div className="detail-value">{inscription.nomComplet || '-'}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">رقم الهاتف</div>
              <div className="detail-value">{inscription.telephone || '-'}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">رقم WhatsApp</div>
              <div className="detail-value">{inscription.numeroWhatsApp}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">المستوى</div>
              <div className="detail-value">
                <span className={`niveau-badge niveau-${inscription.niveauScolaire}`}>
                  {inscription.niveauScolaire || '-'}
                </span>
              </div>
            </div>
            {inscription.typeInscription && (
              <div className="detail-item">
                <div className="detail-label">نوع التسجيل</div>
                <div className="detail-value">
                  <span className={`type-badge type-${inscription.typeInscription}`}>
                    {inscription.typeInscription === 'moaskar' ? '🔥 معسكر' : '📚 عرض أساسي'}
                  </span>
                </div>
              </div>
            )}
            {inscription.filiere && (
              <div className="detail-item">
                <div className="detail-label">الشعبة</div>
                <div className="detail-value">
                  {inscription.filiere === 'scientifique' ? 'علوم' : 
                   inscription.filiere === 'economie' ? 'اقتصاد' :
                   inscription.filiere === 'lettres' ? 'آداب' :
                   inscription.filiere === 'litteraire' ? 'أدبي' : inscription.filiere}
                </div>
              </div>
            )}
            {inscription.matieres && inscription.matieres.length > 0 && (
              <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                <div className="detail-label">المواد المختارة</div>
                <div className="detail-value">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {inscription.matieres.map((matiere, index) => (
                      <span key={index} className="matiere-badge" style={{
                        padding: '4px 12px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '16px',
                        fontSize: '0.9em',
                        color: '#1976d2'
                      }}>
                        {matiere === 'tarbiya_islamiya' ? 'التربية الإسلامية' :
                         matiere === 'arabe' ? 'العربية' :
                         matiere === 'francais' ? 'الفرنسية' :
                         matiere === 'sociologie' ? 'الاجتماعيات' :
                         matiere === 'mathematiques' ? 'الرياضيات' :
                         matiere === 'physique' ? 'الفيزياء' :
                         matiere === 'philosophie' ? 'الفلسفة' :
                         matiere === 'anglais' ? 'الإنجليزية' :
                         matiere === 'svt' ? 'علوم الحياة والأرض' :
                         matiere === 'economie' ? 'جميع مواد شعبة الاقتصاد' : matiere}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {inscription.prixTotal > 0 && (
              <div className="detail-item">
                <div className="detail-label">المبلغ الإجمالي</div>
                <div className="detail-value" style={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '1.1em' }}>
                  {inscription.prixTotal}DH
                </div>
              </div>
            )}
            {inscription.typeInscription === 'aard_assasi' && inscription.groupesParMatiere && Object.keys(inscription.groupesParMatiere).length > 0 && (
              <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                <div className="detail-label">روابط المجموعات (حسب المادة)</div>
                <div className="detail-value">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                    {Object.entries(inscription.groupesParMatiere).map(([matiere, lien]) => (
                      <div key={matiere} style={{ 
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          {matiere === 'tarbiya_islamiya' ? 'التربية الإسلامية' :
                           matiere === 'arabe' ? 'العربية' :
                           matiere === 'francais' ? 'الفرنسية' :
                           matiere === 'sociologie' ? 'الاجتماعيات' :
                           matiere === 'mathematiques' ? 'الرياضيات' :
                           matiere === 'physique' ? 'الفيزياء' :
                           matiere === 'philosophie' ? 'الفلسفة' :
                           matiere === 'anglais' ? 'الإنجليزية' :
                           matiere === 'svt' ? 'علوم الحياة والأرض' :
                           matiere === 'economie' ? 'جميع مواد شعبة الاقتصاد' : matiere}
                        </div>
                        <a 
                          href={lien} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            color: '#1976d2',
                            textDecoration: 'none',
                            fontSize: '0.9em',
                            wordBreak: 'break-all'
                          }}
                        >
                          {lien}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="detail-item">
              <div className="detail-label">الحالة</div>
              <div className="detail-value">
                <span className={`status-badge status-${inscription.statut}`}>
                  {statusLabels[inscription.statut] || inscription.statut}
                </span>
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">تاريخ التسجيل</div>
              <div className="detail-value">
                {new Date(inscription.createdAt).toLocaleDateString('ar-MA')}
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="images-section">
            <h3>📸 الصور</h3>
            <div className="images-grid">
              {inscription.screenshotVirement?.filepath && (
                <div className="image-card">
                  <img 
                    src={`${apiUrl}/inscriptions/${inscription._id}/screenshot-virement`}
                    alt="وصل الأداء"
                    onClick={() => window.open(`${apiUrl}/inscriptions/${inscription._id}/screenshot-virement`, '_blank')}
                  />
                  <div className="image-card-label">وصل الأداء</div>
                </div>
              )}
              {inscription.photoFacebook?.filepath && (
                <div className="image-card">
                  <img 
                    src={`${apiUrl}/inscriptions/${inscription._id}/photo-facebook`}
                    alt="صورة فيسبوك"
                    onClick={() => window.open(`${apiUrl}/inscriptions/${inscription._id}/photo-facebook`, '_blank')}
                  />
                  <div className="image-card-label">صورة فيسبوك</div>
                </div>
              )}
              {inscription.imagesTemporaires?.map((img, index) => (
                <div key={index} className="image-card">
                  <img 
                    src={`${apiUrl}/inscriptions/${inscription._id}/images-temp/${index}`}
                    alt={`صورة ${index + 1}`}
                    onClick={() => window.open(`${apiUrl}/inscriptions/${inscription._id}/images-temp/${index}`, '_blank')}
                  />
                  <div className="image-card-label">صورة {index + 1}</div>
                </div>
              ))}
            </div>

            {/* Boutons pour demander renvoi d'images */}
            <div className="image-actions" style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => requestImageResend('recu')}
                disabled={sending}
              >
                🔄 طلب إعادة وصل الأداء
              </button>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => requestImageResend('facebook')}
                disabled={sending}
              >
                🔄 طلب إعادة صورة فيسبوك
              </button>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={sendCustomMessage}
                disabled={sending}
              >
                ✉️ إرسال رسالة
              </button>
              <button 
                className="btn btn-warning btn-sm"
                onClick={deleteConversation}
                disabled={sending}
                title="حذف المحادثة وإعادة تعيين التسجيل"
              >
                🗑️ حذف المحادثة
              </button>
            </div>
          </div>

          {/* Informations de paiement */}
          <div className="payment-info-section" style={{ marginTop: '24px', padding: '16px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
            <h3>💳 معلومات الدفع</h3>
            
            {/* Sélection de l'image du reçu */}
            {receiptImages.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  اختر صورة وصل الأداء:
                </label>
                <select 
                  value={selectedReceiptIndex ?? ''} 
                  onChange={(e) => setSelectedReceiptIndex(e.target.value === '' ? null : parseInt(e.target.value))}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">-- اختر صورة --</option>
                  {receiptImages.map((img) => (
                    <option key={img.index} value={img.index}>
                      {img.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Type de paiement */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                نوع الدفع:
              </label>
              <select 
                value={paymentType} 
                onChange={(e) => {
                  setPaymentType(e.target.value);
                  if (e.target.value !== 'agency') {
                    setAgencyName('');
                  }
                }}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">-- اختر نوع الدفع --</option>
                <option value="bank">🏦 بنك</option>
                <option value="agency">💵 وكالة</option>
              </select>
            </div>
            
            {/* Nom de l'agence (si agence sélectionnée) */}
            {paymentType === 'agency' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  اسم الوكالة:
                </label>
                <select 
                  value={agencyName} 
                  onChange={(e) => setAgencyName(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">-- اختر الوكالة --</option>
                  <option value="wafacash">Wafacash</option>
                  <option value="barid-cash">Barid Cash</option>
                  <option value="chaabi-cash">Chaabi Cash</option>
                  <option value="damane-cash">Damane Cash</option>
                  <option value="cash-plus">Cash Plus</option>
                </select>
              </div>
            )}
            
            {/* Affichage des informations actuelles */}
            {inscription.paymentInfo?.paymentType && (
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                <strong>المعلومات المحفوظة:</strong>
                <div style={{ marginTop: '8px' }}>
                  <div>نوع الدفع: {inscription.paymentInfo.paymentType === 'bank' ? '🏦 بنك' : '💵 وكالة'}</div>
                  {inscription.paymentInfo.agencyName && (
                    <div>اسم الوكالة: {inscription.paymentInfo.agencyName}</div>
                  )}
                  {inscription.paymentInfo.selectedReceiptIndex !== null && receiptImages[inscription.paymentInfo.selectedReceiptIndex] && (
                    <div>صورة الوصل المختارة: {receiptImages[inscription.paymentInfo.selectedReceiptIndex].label}</div>
                  )}
                </div>
              </div>
            )}
            
            {/* Bouton de sauvegarde */}
            <button 
              className="btn btn-primary"
              onClick={savePaymentInfo}
              disabled={sending || !paymentType || (paymentType === 'agency' && !agencyName)}
              style={{ marginTop: '12px' }}
            >
              💾 حفظ معلومات الدفع
            </button>
          </div>

          {/* Filiere Select for validation - pour 1BAC et 2BAC */}
          {!['valide', 'rejete'].includes(inscription.statut) && showFiliereSelect && (
            <div className="filiere-select-group">
              <label>الشعبة (للمجموعة):</label>
              <select value={filiere} onChange={(e) => setFiliere(e.target.value)}>
                <option value="scientifique">علمي</option>
                <option value="litteraire">أدبي</option>
              </select>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {/* Indicateur d'image en attente */}
          {hasPendingImageRequest && (
            <div className="pending-image-warning" style={{
              width: '100%',
              marginBottom: '12px',
              padding: '12px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              color: '#856404',
              textAlign: 'center'
            }}>
              ⚠️ في انتظار صورة {pendingImageType === 'facebook' ? 'فيسبوك' : 'وصل الأداء'} - لا يمكن التأكيد قبل استلامها
            </div>
          )}
          
          {/* Boutons de validation pour tous les statuts sauf validé/rejeté */}
          {!['valide', 'verifie', 'rejete'].includes(inscription.statut) && (
            <>
              {/* Bouton d'acceptation masqué si image en attente */}
              {!hasPendingImageRequest && (
                <button 
                  className="btn btn-success"
                  onClick={() => {
                    console.log('Validating:', inscription._id, filiere);
                    onValidate(inscription._id, filiere);
                  }}
                  disabled={isProcessing}
                >
                  {isProcessing ? '⏳ جاري المعالجة...' : '✅ تأكيد التسجيل'}
                </button>
              )}
              <button 
                className="btn btn-danger"
                onClick={() => {
                  console.log('Rejecting:', inscription._id);
                  onReject(inscription._id);
                }}
                disabled={isProcessing}
              >
                {isProcessing ? '⏳ جاري المعالجة...' : '❌ رفض'}
              </button>
            </>
          )}
          
          {/* Bouton pour marquer que l'étudiant a rejoint les groupes */}
          {['valide', 'verifie'].includes(inscription.statut) && 
           !inscription.hasJoinedGroups && 
           onMarkJoinedGroups && (
            <button 
              className="btn btn-primary"
              onClick={() => {
                console.log('Marking joined groups:', inscription._id);
                onMarkJoinedGroups(inscription._id);
              }}
              disabled={isProcessing}
              style={{ marginTop: '10px' }}
            >
              {isProcessing ? '⏳ جاري المعالجة...' : '✅ تأكيد انضمام الطالب إلى المجموعات'}
            </button>
          )}
          
          <button className="btn btn-secondary" onClick={onClose}>
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
